import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Copy, FileUp, X } from 'lucide-react';
import { addDoc, collection, doc, limit, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../services/firebase';
import './UserNotifications.css';

const UserNotifications = ({ isCollapsed = false }) => {
  const { currentUser, userData } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [sendingError, setSendingError] = useState(false);
  const [errorForm, setErrorForm] = useState({ title: 'Erro no sistema', message: '' });
  const initialized = useRef(false);
  const seenIds = useRef(new Set());

  const companyId = userData?.role === 'collaborator'
    ? userData?.currentCompanyId
    : userData?.companyId;

  useEffect(() => {
    if (!currentUser?.uid) {
      setNotifications([]);
      return undefined;
    }

    const handleSnapshot = (snapshot, isRootNotification = false) => {
      const next = snapshot.docs
        .map(item => ({ id: item.id, ...item.data(), isRootNotification }))
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        });

      if (initialized.current && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        next
          .filter(item => !item.read && !seenIds.current.has(item.id))
          .forEach(item => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.ready.then(registration => registration.showNotification(item.title, {
                body: item.message,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: item.id,
                data: { url: item.actionPath || '/pos' }
              })).catch(() => {});
            }
          });
      }

      next.forEach(item => seenIds.current.add(item.id));
      initialized.current = true;
      setNotifications(next);
    };

    const unsubscribers = [];
    if (companyId) {
      const notificationsQuery = query(
        collection(db, `companies/${companyId}/userNotifications`),
        where('recipientUserId', '==', currentUser.uid)
      );
      unsubscribers.push(onSnapshot(notificationsQuery, snapshot => handleSnapshot(snapshot), error => {
        console.error('Erro ao carregar notificações:', error);
      }));
    }
    if (userData?.role === 'root') {
      unsubscribers.push(onSnapshot(query(collection(db, 'rootNotifications'), limit(50)), snapshot => handleSnapshot(snapshot, true), error => {
        console.error('Erro ao carregar notificações do Root:', error);
      }));
    }

    return () => unsubscribers.forEach(unsubscribe => unsubscribe());
  }, [companyId, currentUser?.uid, userData?.role]);

  const unreadCount = notifications.filter(item => !item.read).length;

  const requestPermission = async () => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.warn('Permissão de notificação não concedida:', error);
      }
    }
  };

  const togglePanel = async () => {
    setOpen(current => !current);
    await requestPermission();
  };

  const openNotification = async (notification) => {
    if (!notification.read) {
      const notificationRef = notification.isRootNotification
        ? doc(db, 'rootNotifications', notification.id)
        : doc(db, `companies/${companyId}/userNotifications`, notification.id);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
    }
    setOpen(false);
    setSelectedNotification({ ...notification, read: true });
  };

  const copyPixKey = async value => {
    await navigator.clipboard.writeText(value);
    alert('Chave PIX copiada.');
  };

  const uploadReceipt = async event => {
    const file = event.target.files?.[0];
    const announcementId = selectedNotification?.metadata?.announcementId;
    if (!file || !announcementId) return;
    if (!['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('Envie um comprovante em PDF, JPG, PNG ou WEBP.');
      event.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 8 MB.');
      event.target.value = '';
      return;
    }
    setUploadingReceipt(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const receiptRef = ref(storage, `receipts/${companyId}/billing/${announcementId}/${currentUser.uid}-${Date.now()}-${safeName}`);
      await uploadBytes(receiptRef, file, { contentType: file.type });
      const receiptUrl = await getDownloadURL(receiptRef);
      const receiptData = {
        companyId,
        companyName: selectedNotification?.metadata?.companyName || '',
        announcementId,
        receiptUrl,
        receiptName: file.name,
        receiptType: file.type,
        receiptSubmittedAt: serverTimestamp(),
        receiptSubmittedBy: currentUser.uid,
        receiptSubmittedByName: userData?.name || userData?.email || ''
      };

      await setDoc(doc(db, `companies/${companyId}/billingReceipts`, announcementId), receiptData);
      await addDoc(collection(db, 'rootNotifications'), {
        type: 'billing_receipt',
        companyId,
        companyName: receiptData.companyName,
        title: 'Novo comprovante recebido',
        message: `${receiptData.companyName || 'Uma empresa'} enviou o comprovante ${file.name}.`,
        receiptUrl,
        receiptName: file.name,
        receiptType: file.type,
        senderUserId: currentUser.uid,
        senderName: receiptData.receiptSubmittedByName,
        read: false,
        createdAt: serverTimestamp()
      });
      try {
        await updateDoc(doc(db, 'systemAnnouncements', announcementId), receiptData);
      } catch (announcementError) {
        // O registro da empresa já garante que o Root receba o alerta mesmo para anúncios legados.
        console.warn('Anúncio não pôde ser atualizado; comprovante mantido na empresa:', announcementError);
      }
      setSelectedNotification(current => ({ ...current, receiptUrl, receiptName: file.name }));
      alert('Comprovante enviado ao administrador root.');
    } catch (error) {
      console.error('Erro ao enviar comprovante:', error);
      alert('Não foi possível enviar o comprovante: ' + error.message);
    } finally {
      setUploadingReceipt(false);
      event.target.value = '';
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter(item => !item.read);
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    unread.forEach(item => {
      const notificationRef = item.isRootNotification
        ? doc(db, 'rootNotifications', item.id)
        : doc(db, `companies/${companyId}/userNotifications`, item.id);
      batch.update(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
    });
    await batch.commit();
  };

  const sendSystemError = async event => {
    event.preventDefault();
    if (!companyId || !errorForm.message.trim()) return;
    setSendingError(true);
    try {
      await addDoc(collection(db, 'rootNotifications'), {
        type: 'system_error',
        companyId,
        companyName: userData?.companyName || '',
        senderUserId: currentUser.uid,
        senderName: userData?.name || userData?.email || currentUser.email || '',
        title: errorForm.title.trim() || 'Erro no sistema',
        message: errorForm.message.trim(),
        read: false,
        createdAt: serverTimestamp()
      });
      setErrorForm({ title: 'Erro no sistema', message: '' });
      setShowErrorModal(false);
      alert('Relato enviado ao Root.');
    } catch (error) {
      alert('Não foi possível enviar o relato: ' + error.message);
    } finally {
      setSendingError(false);
    }
  };

  const formatDate = value => {
    if (!value) return 'Agora';
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return 'Agora';
    return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`user-notifications ${isCollapsed ? 'is-collapsed' : ''}`}>
      <button
        type="button"
        className="user-notifications__trigger"
        onClick={togglePanel}
        aria-label="Notificações"
        title="Notificações"
      >
        <Bell size={21} />
        {unreadCount > 0 && <span>{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <section className="user-notifications__panel" aria-label="Central de notificações">
          <header>
            <div>
              <strong>Notificações</strong>
              <span>{unreadCount} não lida(s)</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar"><X size={19} /></button>
          </header>
          {unreadCount > 0 && (
            <button type="button" className="user-notifications__read-all" onClick={markAllAsRead}>
              <CheckCheck size={17} />
              Marcar todas como lidas
            </button>
          )}
          {userData?.role === 'admin' && <button type="button" className="user-notifications__report-error" onClick={() => setShowErrorModal(true)}>Relatar erro ao Root</button>}
          <div className="user-notifications__list">
            {notifications.slice(0, 20).map(notification => (
              <button
                type="button"
                key={notification.id}
                className={notification.read ? '' : 'is-unread'}
                onClick={() => openNotification(notification)}
              >
                <span className="user-notifications__dot" />
                <div>
                  <strong>{notification.title}</strong>
                  <p>{notification.message}</p>
                  <small>{formatDate(notification.createdAt)}</small>
                </div>
              </button>
            ))}
            {notifications.length === 0 && <p className="user-notifications__empty">Nenhuma notificação.</p>}
          </div>
        </section>
      )}

      {selectedNotification && <div className="notification-detail-overlay" onClick={() => setSelectedNotification(null)}>
        <section className="notification-detail" onClick={event => event.stopPropagation()}>
          <header><div><span>{selectedNotification.type === 'billing_due' ? 'Cobrança' : 'Comunicado'}</span><h2>{selectedNotification.title}</h2></div><button type="button" onClick={() => setSelectedNotification(null)} aria-label="Fechar"><X size={21} /></button></header>
          <div className="notification-detail__body">
            <p>{selectedNotification.message}</p>
            {selectedNotification.type === 'billing_receipt' && selectedNotification.receiptUrl && (selectedNotification.receiptType === 'application/pdf' ? <iframe className="notification-detail__file-preview" src={selectedNotification.receiptUrl} title="Comprovante recebido" /> : <img className="notification-detail__image-preview" src={selectedNotification.receiptUrl} alt="Comprovante recebido" />)}
            {selectedNotification.metadata?.dueDate && <div className="notification-detail__row"><span>Vencimento</span><strong>{new Date(`${selectedNotification.metadata.dueDate}T12:00:00`).toLocaleDateString('pt-BR')}</strong></div>}
            {selectedNotification.metadata?.amount != null && <div className="notification-detail__row"><span>Valor</span><strong>{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedNotification.metadata.amount)}</strong></div>}
            {selectedNotification.metadata?.pixKey && <div className="notification-detail__pix"><span>Chave PIX</span><div><code>{selectedNotification.metadata.pixKey}</code><button type="button" onClick={() => copyPixKey(selectedNotification.metadata.pixKey)} title="Copiar chave PIX"><Copy size={18} /></button></div></div>}
            {selectedNotification.type === 'billing_due' && selectedNotification.metadata?.announcementId && <label className="notification-receipt-upload"><FileUp size={20} /><span><strong>{uploadingReceipt ? 'Enviando comprovante...' : selectedNotification.receiptName || 'Enviar comprovante'}</strong><small>PDF ou imagem, até 8 MB</small></span><input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={uploadingReceipt} onChange={uploadReceipt} /></label>}
          </div>
          <footer><small>{formatDate(selectedNotification.createdAt)}</small><button type="button" onClick={() => setSelectedNotification(null)}>Fechar</button></footer>
        </section>
      </div>}

      {showErrorModal && <div className="notification-detail-overlay" onClick={() => setShowErrorModal(false)}>
        <form className="notification-detail notification-error-form" onSubmit={sendSystemError} onClick={event => event.stopPropagation()}>
          <header><div><span>Suporte interno</span><h2>Relatar erro ao Root</h2></div><button type="button" onClick={() => setShowErrorModal(false)} aria-label="Fechar"><X size={21} /></button></header>
          <div className="notification-detail__body"><label><span>Título</span><input value={errorForm.title} onChange={event => setErrorForm(current => ({ ...current, title: event.target.value }))} /></label><label><span>Descreva o erro</span><textarea required rows="6" value={errorForm.message} onChange={event => setErrorForm(current => ({ ...current, message: event.target.value }))} placeholder="Informe o que aconteceu, onde ocorreu e como reproduzir." /></label></div>
          <footer><button type="button" onClick={() => setShowErrorModal(false)}>Cancelar</button><button type="submit" disabled={sendingError}>{sendingError ? 'Enviando...' : 'Enviar ao Root'}</button></footer>
        </form>
      </div>}
    </div>
  );
};

export default UserNotifications;
